import classNames from 'classnames';
import { ComponentPropsWithRef, PropsWithChildren, useId } from 'react';
import { Icon } from 'src/components';
import { twMerge } from 'tailwind-merge';

interface Props extends PropsWithChildren<ComponentPropsWithRef<'input'>> {
  value?: string | number;
  defaultStatus?: boolean;
  defaultMessage?: string;
  successStatus?: boolean;
  successMessage?: string;
  errorStatus?: boolean;
  errorMessage?: string;
  floatingLabel?: boolean;
  pill?: boolean;
  classnames?: string;
  numericOnly?: boolean;
  showCalendarIcon?: boolean;
}

const InputText = (props: Props) => {
  const {
    showCalendarIcon,
    defaultStatus,
    defaultMessage,
    errorStatus,
    errorMessage,
    successStatus,
    successMessage,
    floatingLabel,
    pill,
    numericOnly,
    ...oriProps
  } = props;
  const randId = useId();
  let statusClass = 'border-gray-400 focus:ring-black';
  let statusMessageClass = 'text-gray-400';
  let statusIcon = (
    <Icon
      name="circle-exclaimation"
      className="fill-gray-400 w-3 h-3 min-w-max"
      style={{ marginTop: '1px' }}
    />
  );
  let statusMessage: string | undefined = defaultMessage;

  if (successStatus) {
    statusClass = 'border-green-400 focus:ring-green-200';
    statusMessageClass = 'text-green-400';
    statusIcon = (
      <Icon
        name="circle-tick-outline"
        className="fill-green-400 w-3 h-3 min-w-max"
        style={{ marginTop: '1px' }}
      />
    );
    statusMessage = successMessage;
  }

  if (errorStatus) {
    statusClass = 'border-red-400 focus:ring-red-400';
    statusMessageClass = 'text-red-400';
    statusIcon = (
      <Icon
        name="triangle-exclaimation"
        className="fill-red-400 w-3 h-3 min-w-max"
        style={{ marginTop: '1px' }}
      />
    );
    statusMessage = errorMessage;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (numericOnly) {
      const val = e.target.value;
      if (!/^\d*$/.test(val)) return; // check if input is numeric only
    }
    oriProps.onChange?.(e);
  };
  return (
    <div className="relative">
      <input
        id={randId}
        type="text"
        className={twMerge(
          classNames(
            'block rounded-lg w-full max-w-xl text-sm text-gray-900 appearance-none focus:outline-none border focus:border-transparent focus:ring-2 peer',
            floatingLabel === undefined
              ? pill
                ? 'px-5 pb-2.5 pt-5'
                : 'px-2.5 pb-2.5 pt-5'
              : 'p-4',
            pill && 'rounded-full',
            statusClass,
            props.classnames
          )
        )}
        {...oriProps}
        onChange={handleChange}
        placeholder={floatingLabel === undefined ? ' ' : props.placeholder}
      />
      {showCalendarIcon && (
        <Icon
          name="calendar"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
        />
      )}

      {floatingLabel ?? (
        <label
          htmlFor={randId}
          className={twMerge(
            classNames(
              'absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75',
              'top-4 z-1 origin-[0] peer-focus:text-gray-400 peer-focus:dark:text-gray-400',
              'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4',
              pill ? 'left-5' : 'left-2.5'
            )
          )}
        >
          {oriProps.placeholder}
        </label>
      )}
      {successStatus || errorStatus || defaultStatus ? (
        <div className="flex gap-x-1 items-start mt-1">
          {statusIcon}
          <span className={`text-xs ${statusMessageClass}`}>
            {statusMessage}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default InputText;
